import { ProductInterface } from '@bytel/product-wall/types';

import { DiscountType } from '@app-types/discount';
import { EligibilityOptionType } from '@app-types/eligibility';
import { UpsellItemsConfiguration } from '@app-types/eligibility/config';
import { FaiType, FaiTypeNugget, FaiTypeOptions, ObligationType } from '@app-types/plan';
import { PriceType } from '@app-types/price';
import { ProductType } from '@app-types/product';

import { isSameRangeAndPlay } from '@helpers/fai';

import { MarketingType, PlanTypeId } from '@constants/plan';
import { PLAY, TECHNOLOGY } from '@constants/provider';

export interface RawEligibilityOffer {
    type: ProductType | undefined;
    name: string;
    in_stock: boolean;
    price: number;
    rental_fees?: number;
    promotion?: {
        amount: number;
        duration: number;
    };
    speed_down: string;
    speed_up: string;
    obligation?: ObligationType;
    obligationLabel?: string;
    technology: TECHNOLOGY;
    play: PLAY;
    fai_category_type: string;
    sku: string;
    old_price?: number;
    discounts: DiscountType[];
    categories: PlanTypeId[];
    rangeNg: string;
    commercialSeries: string;
    range: string;
    neededElements?: { gencode: string; label: string }[];
    url: string;
    image: string;
    hat?: string;
    subtitle?: string;
    detailsUrl?: string;
    nuggets?: FaiTypeNugget[];
    speedOption?: EligibilityOptionType;
    recommended: boolean;
    isDefault: boolean;
    options?: FaiTypeOptions[];
    prices: PriceType;
    stickers?: ProductInterface['stickers'];
}

export class EligibilityOffer {
    // Old ROC Properties
    type: ProductType | undefined;
    name: string;
    in_stock: boolean;
    price: number;
    rental_fees?: number;
    promotion?: {
        amount: number;
        duration: number;
    };
    speed_down: string;
    speed_up: string;
    obligation?: ObligationType;
    technology: TECHNOLOGY;
    play: PLAY;
    fai_category_type: string;
    sku: string;
    old_price?: number;
    option?: EligibilityOptionType;
    discounts: DiscountType[];
    categories: PlanTypeId[];
    isHidden?: boolean;
    isHiddenInFocus?: boolean;
    rangeNg: string;
    commercialSeries: string;
    range: string;
    neededElements?: { gencode: string; label: string }[];

    // New ROC Properties to enable mapping to FaiType
    url: string;
    image: string;
    hat?: string;
    subtitle?: string;
    detailsUrl?: string;
    nuggets?: FaiTypeNugget[];
    speedOption?: EligibilityOptionType;
    recommended: boolean;
    isDefault: boolean;
    options?: FaiTypeOptions[];
    prices: PriceType;
    stickers?: ProductInterface['stickers'];
    obligationLabel?: string;
    isFallbackMismatchOffer?: boolean;

    public constructor(
        offer: RawEligibilityOffer,
        upsellOffers: UpsellItemsConfiguration[],
        option?: EligibilityOptionType,
    ) {
        this.type = offer.type;
        this.name = offer.name;
        this.in_stock = offer.in_stock;
        this.price = offer.price ?? 0;
        this.rental_fees = offer.rental_fees;
        this.promotion = offer.promotion;
        this.speed_down = offer.speed_down;
        this.speed_up = offer.speed_up;
        this.obligation = offer.obligation;
        this.obligationLabel = offer.obligationLabel;
        this.technology = offer.technology;
        this.play = offer.play;
        this.fai_category_type = offer.fai_category_type;
        this.sku = offer.sku;
        this.old_price = offer.old_price;
        this.option = option;
        this.speedOption = option;
        this.discounts = offer.discounts;
        this.categories = offer.categories;
        this.rangeNg = offer.rangeNg;
        this.commercialSeries = offer.commercialSeries;
        this.range = offer.range;
        this.neededElements = offer.neededElements;
        this.url = offer.url;
        this.image = offer.image;
        this.hat = offer.hat;
        this.subtitle = offer.subtitle;
        this.detailsUrl = offer.detailsUrl;
        this.nuggets = offer.nuggets;
        this.recommended = offer.recommended;
        this.isDefault = offer.isDefault;
        this.options = offer.options;
        this.prices = offer.prices;
        this.stickers = offer.stickers;

        this.initialize(upsellOffers);
        return this;
    }

    public get(key: keyof EligibilityOffer) {
        return this[key];
    }

    private initialize(upsellOffers: UpsellItemsConfiguration[]) {
        this.isHiddenInFocus = this.shouldHideInFocus(upsellOffers);
        this.isHidden = this.shouldHide(upsellOffers);

        // @TODO: move in mapToFai()
        if (this.type === ProductType.FAIM_UNLIMITED) {
            if (this.technology === TECHNOLOGY.BOX_4G) {
                this.speed_up = '38\u00A0Mb/s';
                this.speed_down = '220\u00A0Mb/s';
            } else {
                this.speed_up = '58\u00A0Mb/s';
                this.speed_down = '1,1\u00A0Gb/s';
            }
        }
    }

    private shouldHide(upsellOffers: UpsellItemsConfiguration[]) {
        return upsellOffers.some(
            ({ baseProduct }) =>
                this.isSameRangeAndPlay(baseProduct.rangeNg, baseProduct.commercialSeries, baseProduct.play) &&
                baseProduct.isHiddenDefault,
        );
    }

    private shouldHideInFocus(upsellOffers: UpsellItemsConfiguration[]): boolean {
        return upsellOffers.some(({ baseProduct, upselledProduct }) => {
            return (
                baseProduct.rangeNg === upselledProduct?.rangeNg &&
                baseProduct.commercialSeries === upselledProduct.commercialSeries &&
                this.isSameRangeAndPlay(upselledProduct.rangeNg, upselledProduct.commercialSeries, upselledProduct.play)
            );
        });
    }

    public isSameRangeAndPlay(rangeNg: string, commercialSeries: string, play?: PLAY): boolean {
        return isSameRangeAndPlay(this, rangeNg, commercialSeries, play);
    }

    public isSameOldRange(oldRange: string): boolean {
        return oldRange === (this.range || this.commercialSeries);
    }

    public isSameRangeOrOldRange(rangeNgCommercialSeries: string): boolean {
        return this.isSameRangeAndPlay(rangeNgCommercialSeries, '') || this.isSameOldRange(rangeNgCommercialSeries);
    }

    public hasBigDiscount(): boolean {
        return this.discounts.some((discount) => discount.marketingTypes.includes(MarketingType.HOME));
    }

    public mapToFaiType(): FaiType {
        return {
            id: this.sku,
            gencode: this.sku,
            image: this.image,
            recommended: this.recommended,
            isDefault: this.isDefault,
            type: this.type,
            urlKey: this.url,
            name: this.name,
            categories: this.categories,
            inStock: this.in_stock,
            details: {
                price: this.prices,
                discounts: this.discounts,
                fundings: [],
            },
            rentalFees: this.rental_fees ?? 0,
            downRates: this.speed_down,
            upRates: this.speed_up,
            obligation: this.obligation,
            obligationLabel: this.obligationLabel,
            categoryType: this.fai_category_type,
            play: this.play,
            technology: this.technology,
            rangeNg: this.rangeNg,
            commercialSeries: this.commercialSeries,
            range: this.range,
            hat: this.hat,
            subtitle: this.subtitle,
            detailsUrl: this.detailsUrl,
            nuggets: this.nuggets,
            speedOption: this.speedOption,
            options: this.options,
            stickers: this.stickers,
        };
    }
}

export class EligibilityOfferFactory {
    public static createFromOffer(
        plan: FaiType,
        upsellOffers: UpsellItemsConfiguration[],
        option?: EligibilityOptionType,
    ) {
        const eligibilityOffer: RawEligibilityOffer = {
            type: plan.type,
            name: plan.name ?? '',
            categories: plan.categories as PlanTypeId[],
            in_stock: plan.inStock ?? false,
            price: plan.details.price.initial,
            prices: plan.details.price,
            rental_fees: plan.rentalFees,
            sku: plan.gencode || '',
            speed_down: plan.downRates,
            speed_up: plan.upRates,
            obligation: plan.obligation,
            obligationLabel: plan.obligationLabel,
            fai_category_type: plan.categoryType,
            play: plan.play,
            discounts: plan.details?.discounts ?? [],
            technology: plan.technology,
            rangeNg: plan.rangeNg,
            commercialSeries: plan.commercialSeries,
            range: plan.range,
            url: plan.urlKey,
            image: plan.image,
            hat: plan.hat,
            subtitle: plan.subtitle,
            detailsUrl: plan.detailsUrl,
            nuggets: plan.nuggets,
            speedOption: plan.speedOption,
            recommended: plan.recommended,
            isDefault: plan.isDefault,
            options: plan.options,
            stickers: plan.stickers,
            neededElements: plan.neededElements,
        };

        return new EligibilityOffer(eligibilityOffer, upsellOffers, option);
    }
}
